import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  Platform,
  FlatList as RNFlatList,
  useWindowDimensions,
  ViewToken,
} from 'react-native';
import { FlatList as GHFlatList } from 'react-native-gesture-handler';
import { Feather } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import CachedImage from './CachedImage';
import ImageViewer from './ImageViewer';

// Use gesture handler's FlatList on native for better swipe handling
const FlatList = Platform.OS === 'web' ? RNFlatList : GHFlatList;

interface ImageCarouselProps {
  images: string[];
  height: number;
  onImageLoad?: () => void;
}

export default function ImageCarousel({ images, height, onImageLoad }: ImageCarouselProps) {
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);

  const openViewer = useCallback((index: number) => {
    setViewerInitialIndex(index);
    setViewerVisible(true);
  }, []);

  const closeViewer = useCallback(() => {
    setViewerVisible(false);
  }, []);

  // Calculate image width based on container
  const imageWidth = Platform.OS === 'web'
    ? Math.min(windowWidth - 32, 680 - 32) // Max content width minus padding
    : windowWidth - 32; // Screen width minus padding

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [currentIndex, images.length]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length > 0 && viewableItems[0].index !== null) {
        setCurrentIndex(viewableItems[0].index);
      }
    },
    []
  );

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 50,
  }).current;

  const renderItem = useCallback(
    ({ item, index }: { item: string; index: number }) => (
      <Pressable
        style={[styles.imageContainer, { width: imageWidth, height }]}
        onPress={() => openViewer(index)}
      >
        <CachedImage
          uri={item}
          style={styles.image}
          contentFit="contain"
          onLoad={onImageLoad}
        />
      </Pressable>
    ),
    [imageWidth, height, onImageLoad, openViewer]
  );

  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: imageWidth,
      offset: imageWidth * index,
      index,
    }),
    [imageWidth]
  );

  if (images.length === 0) {
    return null;
  }

  // Single image - no carousel needed
  if (images.length === 1) {
    return (
      <>
        <Pressable style={[styles.container, { height }]} onPress={() => openViewer(0)}>
          <CachedImage
            uri={images[0]}
            style={styles.singleImage}
            contentFit="contain"
            onLoad={onImageLoad}
          />
        </Pressable>
        <ImageViewer
          images={images}
          initialIndex={viewerInitialIndex}
          visible={viewerVisible}
          onClose={closeViewer}
        />
      </>
    );
  }

  return (
    <View style={[styles.container, { height }]}>
      <FlatList
        ref={flatListRef}
        data={images}
        renderItem={renderItem}
        keyExtractor={(item, index) => `${item}-${index}`}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        getItemLayout={getItemLayout}
        snapToInterval={imageWidth}
        decelerationRate="fast"
        bounces={false}
      />

      {/* Indicators */}
      <View style={styles.indicatorContainer}>
        <View style={[styles.indicatorBackground, { backgroundColor: 'rgba(0,0,0,0.5)' }]}>
          {images.map((_, index) => (
            <View
              key={index}
              style={[
                styles.indicator,
                {
                  backgroundColor: index === currentIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                },
              ]}
            />
          ))}
        </View>
      </View>

      {/* Web chevrons */}
      {Platform.OS === 'web' && images.length > 1 && (
        <>
          {currentIndex > 0 && (
            <Pressable
              style={[styles.chevronButton, styles.chevronLeft]}
              onPress={handlePrevious}
            >
              <View style={[styles.chevronBackground, { backgroundColor: 'rgba(0,0,0,0.6)' }]}>
                <Feather name="chevron-left" size={24} color="#fff" />
              </View>
            </Pressable>
          )}
          {currentIndex < images.length - 1 && (
            <Pressable
              style={[styles.chevronButton, styles.chevronRight]}
              onPress={handleNext}
            >
              <View style={[styles.chevronBackground, { backgroundColor: 'rgba(0,0,0,0.6)' }]}>
                <Feather name="chevron-right" size={24} color="#fff" />
              </View>
            </Pressable>
          )}
        </>
      )}

      <ImageViewer
        images={images}
        initialIndex={viewerInitialIndex}
        visible={viewerVisible}
        onClose={closeViewer}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  },
  imageContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  singleImage: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  indicatorContainer: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  indicatorBackground: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 12,
    gap: 6,
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  chevronButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -20,
    zIndex: 10,
  },
  chevronLeft: {
    left: 8,
  },
  chevronRight: {
    right: 8,
  },
  chevronBackground: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
